'use client';

/**
 * @file Defines a StateStorage implementation using IndexedDB for Zustand persistence.
 */

import { del, get, set } from 'idb-keyval';
import { StateStorage } from 'zustand/middleware';

/**
 * State storage implementation using IndexedDB for Zustand persistence.
 */
export const idbStorage: StateStorage = {
  /**
   * Retrieves an item from IndexedDB storage.
   * @param name - The key of the item to retrieve.
   * @returns A promise that resolves to the value of the item or null if not found.
   */
  getItem: async (name: string): Promise<string | null> => {
    return (await get(name)) || null;
  },

  /**
   * Sets an item in IndexedDB storage.
   * @param name - The key of the item to set.
   * @param value - The value to set.
   * @returns A promise that resolves when the item is set.
   */
  setItem: async (name: string, value: string): Promise<void> => {
    await set(name, value);
  },

  /**
   * Removes an item from IndexedDB storage.
   * @param name - The key of the item to remove.
   * @returns A promise that resolves when the item is removed.
   */
  removeItem: async (name: string): Promise<void> => {
    await del(name);
  }
};
